<template>
  <div class="uk-container">
    <template v-if="!ticket">
      <div class="uk-alert-danger uk-width-medium uk-margin-auto uk-text-center" uk-alert>
        <p>Oops! <br> Something went wrong.</p>
      </div>
    </template>

    <template v-else>
      <div class="uk-card uk-card-default uk-width-large uk-margin-auto">
        <div class="uk-card-header uk-text-center">
          <h3 class="uk-card-title">Ticket</h3>
          <p class="uk-text-meta">{{ ticket.id }}</p>
        </div>

        <div class="uk-card-body uk-text-center uk-padding-remove card-content">
          <div class="section">
            <div class="section-title">Form</div>
            <div class="section-content">
              <div class="item">
                <div class="item-title">Name</div>
                <div class="item-content">
                  {{ ticket.content.name }}
                </div>
              </div>
              <div class="item">
                <div class="item-title">Email</div>
                <div class="item-content">
                  {{ ticket.content.email }}
                </div>
              </div>
              <div class="item">
                <div class="item-title">Description</div>
                <div class="item-content">
                  {{ ticket.content.description }}
                </div>
              </div>
            </div>
          </div>

          <div class="section">
            <div class="section-title">User</div>
            <div class="section-content">
              <div class="item">
                <div class="item-title">Name</div>
                <div class="item-content">
                  <div class="composite">
                    <div class="composite-part">
                      <div class="value">{{ user.name.first }}</div>
                      <div class="label">First</div>
                    </div>
                    <div class="composite-part">
                      <div class="value">{{ user.name.last }}</div>
                      <div class="label">Last</div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="item">
                <div class="item-title">Email</div>
                <div class="item-content">
                  {{ user.email }}
                </div>
              </div>
              <div class="item">
                <div class="item-title">User Type</div>
                <div class="item-content">
                  {{ $helpers.userTypes[user.type]|capitalize }}
                </div>
              </div>
            </div>
          </div>

          <div class="section">
            <div class="section-title">Info</div>
            <div class="section-content">
              <div class="item">
                <div class="item-title">Status</div>
                <div class="item-content" v-html="formatStatus(ticket.info.status)" />
              </div>
              <div class="item">
                <div class="item-title">Created</div>
                <div class="item-content">
                  {{ $moment(ticket.info.created.timestamp.seconds, 'X').format('DD/MM/YYYY - HH:mm:ss') }}
                </div>
              </div>
              <div class="item">
                <div class="item-title">Updated</div>
                <div class="item-content">
                  {{ ticket.info.updated && ticket.info.updated.timestamp ? $moment(ticket.info.updated.timestamp.seconds, 'X').format('DD/MM/YYYY - HH:mm:ss') : 'N/A' }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="uk-card-footer uk-text-center">
          <button class="uk-button uk-button-warning uk-margin-right" :disabled="ticket.info.status === 'progress'" @click="setStatus('progress')">In Progress</button>
          <button class="uk-button uk-button-primary" :disabled="ticket.info.status === 'resolved'" @click="setStatus('resolved')">Resolved</button>
        </div>
      </div>

      <div class="uk-margin-top history">
        <div class="uk-card uk-card-default uk-width-large uk-margin-auto">
          <div class="uk-card-header uk-text-center">
            <h3 class="uk-card-title">History</h3>
            <p class="uk-text-meta">Logs - User Actions</p>
          </div>

          <div class="uk-card-body uk-padding-remove uk-text-center actions">
            <div v-for="(action, index) in actions" :key="index" class="action">
              <div class="description" :class="{ hover: action.details }">
                <div class="message">
                  {{ action.message.primary }}

                  <template v-if="action.message.secondary">
                    <span v-html="action.message.secondary" />
                  </template>
                </div>

                <div class="info uk-text-meta">
                  <span :uk-tooltip="`title: ${action.info.timestamp.default}; pos: bottom;`">{{ action.info.timestamp.human }}</span> - <b>{{ action.info.user }}</b>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
<script>
export default {
  name: 'ViewTicketSingle',

  computed: {
    ticket() {
      return this.$store.getters['tickets/find'](this.$route.params.id)
    },

    user() {
      return this.$store.getters['users/find'](this.ticket.info.created.user)
    },

    actions() {
      const actions = []
      // Slice is used to make a shallow copy of an array to prevent state mutation and infinite loop.
      const log = this.ticket.log.slice().reverse()

      log.forEach((action) => {
        const { type } = action
        const user = this.getUser(action.user)
        const message = {}
        const info = {
          timestamp: {
            human: this.$moment(action.timestamp.seconds, 'X').fromNow(),
            default: this.$moment(action.timestamp.seconds, 'X').format('DD/MM/YYYY - HH:mm:ss')
          },
          user
        }

        if (type === 'create') {
          message.primary = 'Ticket was created.'
        }

        if (type === 'update') {
          if (action.subtype && action.subtype === 'status') {
            const status = this.$helpers.status.ticket.find((option) => option.id === action.content)

            message.primary = 'Ticket status was updated to'
            message.secondary = `<b>${status.value}</b>.`
          }
        }

        actions.push({ info, message })
      })

      return actions
    }
  },

  methods: {
    getUser(id) {
      const user = this.$store.getters['users/find'](id)
      return (user) ? `${user.name.first} ${user.name.last}` : 'Web'
    },

    formatStatus(data) {
      let label = '<div class="uk-label uk-label-danger">N/A</div>'
      const option = this.$helpers.status.ticket.find((o) => o.id === data)
      label = `<div class="uk-label uk-label-${option.class}">${option.value}</div>`
      return label
    },

    async setStatus(status) {
      const loader = this.$loader.show()
      const ticket = {
        'info.status': status,
        'info.updated': {
          timestamp: this.$firebase.firestore.FieldValue.serverTimestamp(),
          user: this.$store.state.account.record.uid
        },
        log: this.$firebase.firestore.FieldValue.arrayUnion({
          timestamp: this.$firebase.firestore.Timestamp.now(),
          user: this.user.id,
          type: 'update',
          subtype: 'status',
          content: status
        })
      }

      try {
        await this.$firebase.firestore().collection(this.$globals.firebase.collection.tickets).doc(this.ticket.id).update(ticket)
        this.$uikit.notification('Your ticket was updated successfully!', 'success')
      } catch (error) {
        this.$uikit.notification('Something went wrong while updating your ticket. Please try again later, or contact support.', 'danger')
      } finally {
        loader.hide()
      }
    }
  }
}
</script>
